/**
 * Interceptor global de axios (feb 2026 - envío soporte)
 * ---------------------------------------------------------------------------
 * Antes: si el backend devolvía texto plano (500 de FastAPI, HTML de nginx,
 * "Internal Server Error") axios intentaba parsearlo como JSON y rompía la
 * app con `Unexpected token 'I'` o `Unexpected token '<'`.
 *
 * Ahora: verificamos el header `content-type` en cada respuesta a `/api/*`.
 * Si NO es JSON, convertimos la respuesta en un error controlado con un
 * mensaje amigable en español para que los componentes que hacen catch
 * puedan mostrarlo sin romper el render.
 *
 * No modifica endpoints, no cambia el flujo de datos: solo blinda el parseo.
 */
import axios from 'axios';

let installed = false;

export const installAxiosInterceptors = () => {
  if (installed) return;
  installed = true;

  // Interceptor de RESPUESTA
  axios.interceptors.response.use(
    (response) => {
      // Solo verificar respuestas a /api/*
      const url = response?.config?.url || '';
      const isApiCall = url.includes('/api/');
      if (!isApiCall) return response;

      const contentType = (response?.headers?.['content-type'] || '').toLowerCase();
      // Si la respuesta viene marcada como JSON pero el body es un string HTML/texto
      // (axios no lo parseó), o el header directamente no es JSON, la marcamos como error.
      const isJson = contentType.includes('application/json');
      const bodyLooksHtml = typeof response.data === 'string' &&
        (response.data.trimStart().startsWith('<') ||
         response.data.trimStart().toLowerCase().startsWith('internal '));

      if (!isJson || bodyLooksHtml) {
        // eslint-disable-next-line no-console
        console.warn(
          `⚠️ Respuesta no-JSON en ${url} (content-type: ${contentType || 'unknown'}). Tratada como error controlado.`
        );
        const err = new Error('Servicio temporalmente no disponible. Reintentando…');
        err.isNonJsonResponse = true;
        err.originalStatus = response.status;
        err.originalContentType = contentType;
        return Promise.reject(err);
      }
      return response;
    },
    (error) => {
      // Errores HTTP normales: si la respuesta trae un body no-JSON, normalizarlo
      const response = error?.response;
      if (response) {
        const url = response?.config?.url || error?.config?.url || '';
        const isApiCall = url.includes('/api/');
        if (isApiCall) {
          const contentType = (response?.headers?.['content-type'] || '').toLowerCase();
          const isJson = contentType.includes('application/json');
          if (!isJson) {
            // eslint-disable-next-line no-console
            console.warn(
              `⚠️ Error no-JSON en ${url} (status ${response.status}, content-type: ${contentType || 'unknown'}). Normalizado.`
            );
            error.isNonJsonResponse = true;
            error.friendlyMessage = 'Servicio temporalmente no disponible. Reintentando…';
            // Reemplazar el body por un objeto JSON válido para que el resto del código
            // que hace error.response.data.detail no se rompa.
            error.response.data = { detail: 'Service temporarily unavailable' };
          }
        }
      }
      return Promise.reject(error);
    }
  );
};

export default installAxiosInterceptors;
